// @ts-nocheck
import useCheckoutCurrency from '../../../hooks/useCheckoutCurrency';
import { getCheckoutPaymentMethodOrder } from '../../../utils/checkoutCurrency';
import BookingTotals from '../BookingPricing/BookingTotals/BookingTotals';
import BookingStepper from '../BookingStepper/CustomStepper/CustomStepper';
import BookingSuccessful from '../BookingSuccessful/BookingSuccessful';
import BookingPlan from '../BookingPlans/BookingPlan/BookingPlan';
import BookingPayment from '../BookingPayments/BookingPayment/BookingPayment';
import BookingPassengerPanel from '../BookingPassengers/BookingPassengerPanel/BookingPassengerPanel';
import BookingAddOnsPanel from '../BookingAddOns/BookingAddOnsPanel/BookingAddOnsPanel';
import BookingCapacityBanner from '../BookingCapacityBanner/BookingCapacityBanner';
import PlaceholderLogo from '../../../assets/images/PlaceholderLogo';
import { isEmailValid } from '../../../utils/emailValidator';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { initGA, trackEvent } from '../../../utils/analytics';
import { XGModal } from 'xg-cl';
import { preventBubbling } from '../../../utils/eventHelper';
import { matchIsValidTel } from 'mui-tel-input';
import { getTripPassengerFields } from '../../../services/trip/trip';
import { useBookingSteps } from '../../../hooks/useBookingSteps';
import {
  updateActiveStep,
  updatePassengerForms,
  resetFieldErrors,
  updateFormErrors,
  addTotalItems,
  updateTotalItem,
  resetTotals,
  updateAddOnsStepVisibility
} from '../../../redux/slices/booking/booking';
import { loadStripe } from '@stripe/stripe-js';
import { ErrorToast } from '../../../utils/alerts';
import {
  getBookingTotals,
  planIsSoldOut,
  planIsPastDeadline,
  getRemainingCapacity,
  isCapacityExceeded,
  getTotalSelectedPassengers,
  tripHasDeparted
} from '../../../utils/booking';
import {
  getStripeConfig,
  createPaymentIntent,
  cancelPaymentIntent
} from '../../../services/stripe/stripe';
import {
  DATE,
  EMAIL,
  PHONE,
  NUMBER,
  CHECKBOX
} from '../../../constants/passengers';
import {
  getPassengerFieldUploadFiles,
  getPassengerFieldUploadKey,
  hasPassengerFieldUploadSelection,
  isFileUploadPassengerFieldType,
  isPassengerFieldVisibleForBooking,
  isSignableDocumentPassengerFieldType
} from '../../../utils/passengerFieldTypes';
import { format, add } from 'date-fns';
import { parseISODateWithoutOffset } from '../../../utils/dateUtility';
import { Box } from '@mui/material';
import { getFeesBreakdown } from '../../../utils/feesHelper';
import { getSelectedAddOnsTotal } from '../../../utils/percentageAddOns';
import { getOperatorContactLink } from '../../../utils/operatorContact';
import { renderToStaticMarkup } from 'react-dom/server';
import { WarningCircle } from '@phosphor-icons/react';
import BookingPlanActions from '../BookingPlans/BookingPlanActions/BookingPlanActions';
import {
  setSignedDocumentError,
  setCustomFormError,
  setTermsError,
  setIsSubmitting,
  setDocumentStatus,
  setSignedDocumentId,
  setSignedDocumentToken,
  setIsPaymentMethodComplete,
  resetCheckout
} from '../../../redux/slices/checkout/checkout';
import * as PropTypes from 'prop-types';
import './BookingModal.css';

/**
 * Functional React component for rendering a custom booking modal.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the trip output booking element.
 * @param {string} [props.isOpenModal] - Boolean value to indicate whether modal is open.
 * @param {Function} [props.updateIsOpen] - Function to update the open state.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the booking modal component.
 */
const BookingModal = ({
  id,
  isOpenModal,
  updateIsOpen,
  bookingPlans,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['trip', 'common', 'errors']);
  const {
    enableTripCapacity,
    enableAddOns,
    enablePassengerFieldUploads,
    enablePaxWaivers,
    enablePercentageBasedAddons,
    enableMultiCurrency
  } = useFlags();
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [paymentMethodOrder, setPaymentMethodOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentRecoveryStatus, setPaymentRecoveryStatus] = useState(null);
  const [isRestoringIntent, setIsRestoringIntent] = useState(false);
  const [checkoutSessionId, setCheckoutSessionId] = useState(0);
  const [passengerFieldFiles, setPassengerFieldFiles] = useState({});
  const booking = useSelector((state) => state.booking);
  const bookingplans = useMemo(
    () => bookingPlans || booking?.bookingplans || [],
    [bookingPlans, booking?.bookingplans]
  );
  const tenant = booking?.tenant || null;
  const trip = booking?.trip || null;
  const operatorContact = {
    ...(trip?.operatorContact || {}),
    email: trip?.operatorContact?.email || trip?.operatorEmail
  };
  const operatorContactLink = getOperatorContactLink(
    operatorContact,
    trip?.tripname
  );
  const tripCapacity = booking?.tripCapacity ?? null;
  const usedCapacity = booking?.usedCapacity ?? 0;
  // Check if user is logged in (staff) by checking localStorage directly
  // This is more reliable than Redux state since BookingModal may be used on public pages
  const getUserSession = () => {
    try {
      const userSession = localStorage.getItem('userSession');
      return userSession ? JSON.parse(userSession) : null;
    } catch (e) {
      return null;
    }
  };

  // Memoize addons and selectedAddOns to maintain stable references
  const addons = useMemo(() => booking?.addons || [], [booking?.addons]);
  const selectedAddOns = useMemo(
    () => booking?.selectedAddOns || [],
    [booking?.selectedAddOns]
  );

  // Calculate add-ons total
  const totalAddOnsPrice = getSelectedAddOnsTotal({
    selectedAddOns,
    addons,
    passengers: booking?.passengerForms || [],
    bookingPlans: bookingplans,
    enablePercentageBasedAddons
  });

  const userSession = getUserSession();
  const isStaff = Boolean(userSession?.userId);

  const { steps, activeStep, getStepNumber } = useBookingSteps();
  const { totalPrice: totalPlansPrice, totalDeposit } =
    getBookingTotals(bookingplans);

  // Total price including both plans and add-ons
  const totalPrice = totalPlansPrice + totalAddOnsPrice;
  const submitRef = useRef(null);
  const checkoutSessionRef = useRef({
    isOpen: false,
    tripId: null,
    successful: false,
    started: false
  });

  // Trip capacity calculations
  const remainingCapacity =
    enableTripCapacity && tripCapacity !== null
      ? getRemainingCapacity(tripCapacity, bookingplans, usedCapacity)
      : null;
  // Original remaining capacity (ignoring user selections) - for banner display only
  const originalRemainingCapacity =
    enableTripCapacity && tripCapacity !== null
      ? Math.max(0, tripCapacity - usedCapacity)
      : null;
  const capacityExceeded =
    enableTripCapacity && tripCapacity !== null
      ? isCapacityExceeded(tripCapacity, bookingplans, usedCapacity)
      : false;
  const totalSelected = getTotalSelectedPassengers(bookingplans);
  // Only consider it "full capacity" if there are 0 spots AND user hasn't selected anything
  // This allows users to modify their selections even when capacity is fully utilized
  const isFullCapacity =
    enableTripCapacity &&
    remainingCapacity !== null &&
    remainingCapacity === 0 &&
    totalSelected === 0;

  //Trip Data
  const tenantLogo = tenant?.tenantlogo || null;
  const tenantName = tenant?.tenantname || null;
  const tripName = trip?.tripname || null;
  const tripDate = trip?.startdate || null;
  const isTripDeparted = tripHasDeparted(tripDate);
  const tripDuration = trip?.duration || null;
  const startDate = parseISODateWithoutOffset(tripDate);
  const endDate =
    startDate && add(new Date(startDate), { days: tripDuration - 1 });
  const dateFormat = t('common:dateFormat');

  const selectedPlans = bookingplans?.filter(
    (plan) => plan.selectedQuantity > 0
  );

  const filteredSteps = steps?.filter((step) => step.shouldRender);
  const passengerForms = useMemo(
    () => booking?.passengerForms || [],
    [booking?.passengerForms]
  );
  const hasUnavailablePlans =
    isTripDeparted ||
    bookingplans?.some(
      (plan) => planIsSoldOut(plan) || planIsPastDeadline(plan)
    );

  const checkout = useSelector((state) => state.checkout) || {};
  const {
    customForm,
    customFormError,
    documentStatus,
    isTermsChecked,
    requiresSignedDocument,
    signedDocumentId,
    isSubmitting,
    isProcessing,
    isPaymentMethodComplete
  } = checkout;
  const shouldRequireTerms = Boolean(
    !requiresSignedDocument && tenant?.tcenabled
  );

  // GA funnel tracking - track each step only once per modal session
  const [firedBeginCheckout, setFiredBeginCheckout] = useState(false);
  const [firedPassengerStep, setFiredPassengerStep] = useState(false);
  const [firedPaymentStep, setFiredPaymentStep] = useState(false);
  const isZeroTotalBooking = Number(totalPrice || 0) <= 0;
  const isStripePaymentRequired =
    activeStep?.key === 'payment' &&
    !isZeroTotalBooking &&
    booking?.paymentOption !== 'optionnopayment';

  const operatorCurrency = (
    tenant?.sellingcurrency?.currencycode ||
    tenant?.basecurrency?.currencycode ||
    'USD'
  ).toUpperCase();
  const currencyCheckout = useMemo(
    () => ({
      tripId: Number(trip?.tripid),
      tripKey: trip?.tripkey,
      tripplans: bookingplans
        .filter((plan) => Number(plan.selectedQuantity) > 0)
        .map((plan) => ({
          tripplanid: Number(plan.tripplanid),
          occupancy: Number(plan.selectedQuantity)
        })),
      passengers: passengerForms.map((form) => [
        { tripplanid: Number(form.tripplanid) },
        {
          tripaddonids: selectedAddOns
            .filter((addon) => addon.passengerFormIds?.includes(form.formId))
            .map((addon) => Number(addon.tripaddonid))
        }
      ]),
      discountCodes: (booking?.appliedDiscountCodes || []).map(
        (code) => code.code
      ),
      paymentOption:
        booking?.paymentOption === 'optionfullamount' ? 'full' : 'deposit'
    }),
    [
      trip?.tripid,
      trip?.tripkey,
      bookingplans,
      passengerForms,
      selectedAddOns,
      booking?.appliedDiscountCodes,
      booking?.paymentOption
    ]
  );
  const operatorAmount = getFeesBreakdown(
    Number(booking?.amountToCharge || 0),
    trip?.fees ?? [],
    paymentMethod
  ).totalWithFeesInChargeCurrency;
  const handleCurrencyIntentChange = useCallback(
    (intent) => {
      setClientSecret(intent?.clientSecret || null);
      setPaymentIntent(intent?.paymentIntentId || null);
      if (intent?.paymentIntentId !== paymentIntent)
        dispatch(setIsPaymentMethodComplete(false));
    },
    [dispatch, paymentIntent]
  );
  const currencyState = useCheckoutCurrency({
    sessionKey: checkoutSessionId,
    enabled: Boolean(enableMultiCurrency),
    prefetch: Boolean(
      isOpenModal &&
      !isZeroTotalBooking &&
      ['passenger', 'addons'].includes(activeStep?.key)
    ),
    active: Boolean(isOpenModal && isStripePaymentRequired),
    operatorCurrency,
    quoteContext: { tripId: Number(trip?.tripid), tripKey: trip?.tripkey },
    checkout: currencyCheckout,
    initialIntent: { paymentIntentId: paymentIntent, clientSecret },
    operatorAmount,
    operatorBookingCredit: Number(booking?.amountToCharge || 0),
    paymentMethod,
    processing: Boolean(isProcessing || isSubmitting),
    onIntentChange: handleCurrencyIntentChange
  });
  const selectedCurrency =
    currencyState.pendingSelection || currencyState.selection;
  const effectivePaymentMethods =
    (selectedCurrency?.currency !== undefined &&
      selectedCurrency.currency !== operatorCurrency) ||
    operatorCurrency !== 'USD'
      ? (paymentMethodOrder || []).filter(
          (method) => method !== 'us_bank_account'
        )
      : paymentMethodOrder;

  useEffect(() => {
    const tripId = trip?.tripid ?? null;
    const previousSession = checkoutSessionRef.current;
    const isNewSession = isOpenModal && !previousSession.isOpen;
    const isAnotherBooking =
      isOpenModal && previousSession.successful && activeStep?.key === 'plan';
    const didChangeTrips =
      isOpenModal &&
      previousSession.tripId !== null &&
      tripId !== null &&
      previousSession.tripId !== tripId;

    if (isNewSession || didChangeTrips || isAnotherBooking) {
      // A confirmed booking keeps its paid owner locked. The next booking
      // gets a new owner and fresh intent/availability instead of unlocking it.
      if (previousSession.started)
        setCheckoutSessionId((session) => session + 1);
      setClientSecret(null);
      setPaymentIntent(null);
      setPaymentMethod('card');
      setPaymentRecoveryStatus(null);
      setPassengerFieldFiles({});
      dispatch(
        resetCheckout({
          requiresSignedDocument: Boolean(trip?.requiresSignedDocument)
        })
      );
    }

    checkoutSessionRef.current = {
      isOpen: isOpenModal,
      tripId,
      successful: activeStep?.key === 'success',
      started: previousSession.started || Boolean(isOpenModal)
    };
  }, [
    dispatch,
    isOpenModal,
    trip?.tripid,
    trip?.requiresSignedDocument,
    activeStep?.key
  ]);

  // A managed intent can be cancelled when switching to no payment. Re-entering
  // a paid option starts the existing operator-currency path again.
  const restoringIntentRef = useRef(false);
  const modalMountedRef = useRef(true);
  const modalPaymentSessionRef = useRef(null);
  modalPaymentSessionRef.current = {
    isOpenModal,
    isStripePaymentRequired,
    tripId: trip?.tripid
  };
  useEffect(() => {
    modalMountedRef.current = true;
    return () => {
      modalMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (
      !isOpenModal ||
      !isStripePaymentRequired ||
      clientSecret ||
      !stripePromise ||
      currencyState.loading ||
      currencyState.managed ||
      restoringIntentRef.current ||
      operatorAmount <= 0
    )
      return;
    restoringIntentRef.current = true;
    setIsRestoringIntent(true);
    const restorationTripId = trip?.tripid;
    const fees = getFeesBreakdown(
      Number(booking?.amountToCharge || 0),
      trip?.fees ?? [],
      paymentMethod
    );
    createPaymentIntent({
      amount: fees.totalWithFeesInChargeCurrency,
      applicationFees:
        fees.applicationFeeNotIncluded + fees.applicationFeeIncluded,
      description: trip?.tripname,
      paymentMethods: paymentMethodOrder
    })
      .then(async (response) => {
        if (!response?.data) return;
        const session = modalPaymentSessionRef.current;
        if (
          !modalMountedRef.current ||
          !session.isOpenModal ||
          !session.isStripePaymentRequired ||
          session.tripId !== restorationTripId
        ) {
          await cancelPaymentIntent({
            paymentIntentId: response.data.paymentIntentId,
            clientSecret: response.data.clientSecret
          });
          return;
        }
        handleCurrencyIntentChange(response.data);
      })
      .catch(() => ErrorToast(t('errors:bookingPaymentFailed')))
      .finally(() => {
        restoringIntentRef.current = false;
        if (modalMountedRef.current) setIsRestoringIntent(false);
      });
  }, [
    isOpenModal,
    isStripePaymentRequired,
    clientSecret,
    stripePromise,
    currencyState.loading,
    currencyState.managed,
    operatorAmount,
    booking?.amountToCharge,
    trip?.fees,
    trip?.tripname,
    trip?.tripid,
    paymentMethod,
    paymentMethodOrder,
    handleCurrencyIntentChange,
    t
  ]);

  // Helper to get GA tracking context
  const getGAContext = useCallback(() => {
    const tripId = trip?.tripid;
    const tripName = trip?.tripname;
    const gaId = tenant?.googleanalyticsid;
    const currency =
      tenant?.sellingcurrency?.currencycode ||
      tenant?.basecurrency?.currencycode;
    const passengerCount = (passengerForms || []).length;

    return { tripId, tripName, gaId, currency, passengerCount };
  }, [tenant, trip, passengerForms]);

  // Handle add-ons step visibility based on feature flag and available add-ons
  useEffect(() => {
    if (enableAddOns === undefined || !trip?.tripid) return;

    // Check if add-ons step should be visible
    // Only show if enableAddOns flag is true AND there are visible add-ons
    if (enableAddOns) {
      const addons = booking?.addons || [];

      // Staff users (logged in) see all add-ons, public users see filtered add-ons
      const visibleAddons = isStaff
        ? addons // Staff see all add-ons
        : addons.filter((addon) => {
            // Public users respect visibility settings
            if (addon?.ishiddenfrompassengers) return false;

            // Check auto-hide conditions
            if (addon?.autohidewhensoldout && planIsSoldOut(addon))
              return false;
            if (addon?.autohideafterdeadline && planIsPastDeadline(addon))
              return false;

            return true;
          });

      // Show add-ons step only if there are visible add-ons
      dispatch(
        updateAddOnsStepVisibility({ shouldRender: visibleAddons.length > 0 })
      );
    } else {
      // Hide add-ons step if feature flag is disabled
      dispatch(updateAddOnsStepVisibility({ shouldRender: false }));
    }
  }, [enableAddOns, trip?.tripid, booking?.addons, dispatch, isStaff]);

  // GA Event: Track begin_checkout when modal opens on plan step
  useEffect(() => {
    if (!isOpenModal || activeStep?.key !== 'plan' || firedBeginCheckout)
      return;

    const { tripId, tripName, currency, gaId } = getGAContext();
    if (!gaId || !tripId) return;

    initGA(gaId);
    trackEvent('begin_checkout', {
      trip_id: tripId,
      trip_name: tripName,
      currency,
      step: 1,
      alias: 'booking_step_1_plans'
    });

    setFiredBeginCheckout(true);
  }, [isOpenModal, activeStep?.key, firedBeginCheckout, getGAContext]);

  // GA Event: Track add_traveler_info when user reaches passenger step
  useEffect(() => {
    if (!isOpenModal || activeStep?.key !== 'passenger' || firedPassengerStep)
      return;

    const { tripId, passengerCount, gaId } = getGAContext();
    if (!gaId || !tripId) return;

    initGA(gaId);
    trackEvent('add_traveler_info', {
      passenger_count: passengerCount,
      trip_id: tripId,
      step: 2,
      alias: 'booking_step_2_passengers'
    });

    setFiredPassengerStep(true);
  }, [isOpenModal, activeStep?.key, firedPassengerStep, getGAContext]);

  // GA Event: Track add_payment_info when user reaches payment step
  useEffect(() => {
    if (!isOpenModal || activeStep?.key !== 'payment' || firedPaymentStep)
      return;

    const { tripId, gaId } = getGAContext();
    if (!gaId || !tripId) return;

    initGA(gaId);
    // Step number is dynamic based on whether add-ons step is visible
    const paymentStepNumber = getStepNumber('payment');
    trackEvent('add_payment_info', {
      trip_id: tripId,
      step: paymentStepNumber,
      alias: `booking_step_${paymentStepNumber}_payment`
    });

    setFiredPaymentStep(true);
  }, [
    isOpenModal,
    activeStep?.key,
    firedPaymentStep,
    getGAContext,
    getStepNumber
  ]);

  //Display base total on plan and passenger step
  useEffect(() => {
    if (
      (activeStep && activeStep?.key === 'passenger') ||
      activeStep?.key === 'plan'
    ) {
      dispatch(
        addTotalItems({
          totalItems: [
            {
              title: t('trip:tripTotal'),
              priceperperson: totalPrice,
              occupancy: 1,
              depositamount: totalDeposit,
              hideTotalBreakdown: true,
              hideDeposit: true,
              selectedQuantity: 1,
              key: 'trip-total',
              totalPrice: totalPrice,
              order: 3,
              visible: true
            }
          ]
        })
      );
    }
  }, [
    dispatch,
    t,
    totalPrice,
    totalDeposit,
    activeStep,
    booking?.paymentOption
  ]);

  //To avoid "caching" stripe promise, client secret, and payment intent, we are resetting them when user navigates passenger step.
  useEffect(() => {
    if (activeStep && activeStep?.key === 'passenger') {
      setPaymentMethodOrder(null);
      setStripePromise(null);
      setClientSecret(null);
      setPaymentIntent(null);
    }
  }, [activeStep]);

  //Open Booking Modal if openBookingModal query param is true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('openBookingModal') === 'true') {
      updateIsOpen(true);

      params.delete('openBookingModal');
      params.delete('bookingPlanId');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${params.toString()}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToStepper = () => {
    const stepperElement = document.getElementById('book-now-modal-stepper');
    if (stepperElement) {
      stepperElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePassengerFieldFilesChange = useCallback(
    (formId, trippassengerfieldid, files = []) => {
      const uploadKey = getPassengerFieldUploadKey(
        formId,
        trippassengerfieldid
      );

      setPassengerFieldFiles((prev) => {
        const next = { ...prev };

        if (files.length) {
          next[uploadKey] = files;
        } else {
          delete next[uploadKey];
        }

        return next;
      });
    },
    []
  );

  const getFeedbackFromForms = () => {
    const forms = passengerForms || [];
    const formErrors = [];

    for (const form of forms) {
      const errors = getFeedbackFromFields(form?.fields || [], form?.formId);

      if (errors && errors.length) {
        formErrors.push({ errors, formId: form.formId });
        continue;
      }
    }

    return formErrors;
  };

  const getFeedbackFromFields = (fields, formId = '') => {
    const errors = [];

    for (const field of fields) {
      const { value, isrequired, fieldtype, title } = field;
      const isFileUploadField =
        enablePassengerFieldUploads &&
        isFileUploadPassengerFieldType(fieldtype);
      const isSignableDocumentField =
        enablePaxWaivers && isSignableDocumentPassengerFieldType(fieldtype);
      const uploadedFiles = isFileUploadField
        ? getPassengerFieldUploadFiles(
            passengerFieldFiles,
            formId,
            field?.trippassengerfieldid
          )
        : [];
      const hasPassengerFiles = hasPassengerFieldUploadSelection(
        field,
        uploadedFiles
      );

      // Check for required fields
      if (isrequired) {
        if (isSignableDocumentField && !value) {
          errors.push({
            ...field,
            error: t('passengers:pleaseSignPdf')
          });
        } else if (isFileUploadField && !hasPassengerFiles) {
          errors.push({
            ...field,
            error: `${t('common:pleaseEnter')} ${title?.toLowerCase()}.`
          });
        } else if (
          (fieldtype?.key === CHECKBOX &&
            Array.isArray(value) &&
            value?.length === 0) ||
          (typeof value === 'string' && (!value || value?.trim() === '')) ||
          (!value && fieldtype?.key !== CHECKBOX && !isFileUploadField)
        ) {
          errors.push({
            ...field,
            error: `${t('common:pleaseEnter')} ${title?.toLowerCase()}.`
          });
        }
      }

      // Check for valid phone number
      if (
        !isFileUploadField &&
        fieldtype?.key === PHONE &&
        (isrequired || value?.trim().split(' ')?.length > 1)
      ) {
        const valid = matchIsValidTel(value?.trim());

        if (!valid) {
          errors.push({
            ...field,
            error: t('trip:pleaseEnterValidPhoneNumber')
          });
        }
      }

      // Check for valid email
      if (
        !isFileUploadField &&
        fieldtype?.key === EMAIL &&
        value?.trim() !== ''
      ) {
        if (!isEmailValid(value)) {
          errors.push({
            ...field,
            error: t('trip:pleaseEnterValidEmail')
          });
        }
      }

      // Check for valid number
      if (
        !isFileUploadField &&
        fieldtype?.key === NUMBER &&
        value?.trim() !== ''
      ) {
        if (isNaN(value?.trim())) {
          errors.push({
            ...field,
            error: t('trip:pleaseEnterValidNumber')
          });
        }
      }

      // Check for valid date
      if (
        !isFileUploadField &&
        fieldtype?.key === DATE &&
        (isrequired || value?.trim() !== '')
      ) {
        const parsedDate = Date.parse(value?.trim());

        if (isNaN(parsedDate)) {
          errors.push({
            ...field,
            error: t('trip:pleaseEnterValidDate')
          });
        }
      }
    }

    return errors;
  };

  const handleStepClick = async (event, stepIndex) => {
    if (
      isSubmitting ||
      isProcessing ||
      isRestoringIntent ||
      currencyState.loading
    )
      return;
    const activeIndex = steps.findIndex((step) => step.active);

    if (stepIndex > activeIndex) {
      if (activeStep?.key === 'plan') {
        await onNavigateToPassengers(event);
      } else if (activeStep?.key === 'passenger') {
        await onNavigateToAddOnsOrPayment(event);
      } else if (activeStep?.key === 'addons') {
        await onNavigateToPayment(event);
      }
    } else if (stepIndex < activeIndex) {
      if (activeStep?.key === 'payment' && !(await currencyState.reset()))
        return;
      const stepKey = steps[stepIndex]?.key;
      dispatch(updateActiveStep({ key: stepKey }));
    }
  };

  const toggleFeeTotalsVisibility = (visible = false) => {
    dispatch(
      updateTotalItem({
        totalKey: 'trip-subtotal',
        totalItem: {
          visible: visible
        }
      })
    );
    dispatch(
      updateTotalItem({
        totalKey: 'trip-totalfees',
        totalItem: {
          visible: visible
        }
      })
    );
  };

  const onNavigateToPayment = async (event) => {
    try {
      preventBubbling(event);

      dispatch(
        setCustomFormError({
          email: '',
          firstName: '',
          lastName: ''
        })
      );

      dispatch(setDocumentStatus('DRAFT'));
      dispatch(setSignedDocumentId(null));
      dispatch(setSignedDocumentToken(null));
      dispatch(setIsPaymentMethodComplete(false));

      if (selectedPlans.length > 0) {
        dispatch(setIsSubmitting(true));
        dispatch(resetFieldErrors());

        const formErrors = getFeedbackFromForms();

        if (formErrors && formErrors.length) {
          dispatch(updateFormErrors({ forms: formErrors }));
          return;
        }

        const { data: dataConfig } = await getStripeConfig();

        if (dataConfig) {
          const { publishableKey, stripeAccount, paymentMethods } = dataConfig;

          const paymentMethodCodes = getCheckoutPaymentMethodOrder(
            paymentMethods
              ?.map((method) => method?.paymentcode)
              ?.filter(
                (method) =>
                  operatorCurrency === 'USD' || method !== 'us_bank_account'
              ),
            enableMultiCurrency
          );
          setPaymentMethod(paymentMethodCodes?.[0] || 'card');

          if (!publishableKey || !stripeAccount) {
            ErrorToast(t('errors:missingPaymentMethods'));
            return;
          }

          if (!paymentMethodCodes || !paymentMethodCodes?.length) {
            ErrorToast(t('errors:missingPaymentMethods'));
          }

          setPaymentMethodOrder(paymentMethodCodes);
          setStripePromise(
            loadStripe(publishableKey, {
              stripeAccount
            })
          );

          // Create payment intent with deposit if available, otherwise full amount
          // CheckoutForm will update it based on user selection and validate before charge
          const initialAmount = totalDeposit ? totalDeposit : totalPrice;
          const {
            applicationFeeNotIncluded,
            applicationFeeIncluded,
            totalWithFees,
            totalWithFeesInChargeCurrency,
            totalFees
          } = getFeesBreakdown(
            initialAmount,
            trip?.fees ?? [],
            paymentMethodCodes && paymentMethodCodes.length > 0
              ? paymentMethodCodes[0]
              : ''
          );

          if (initialAmount > 0) {
            const { data: dataIntent } = await createPaymentIntent({
              amount: totalWithFeesInChargeCurrency,
              description: trip?.tripname,
              applicationFees:
                applicationFeeNotIncluded + applicationFeeIncluded,
              paymentMethods: paymentMethodCodes
            });

            if (dataIntent) {
              const { clientSecret, paymentIntentId } = dataIntent;
              setClientSecret(clientSecret);
              setPaymentIntent(paymentIntentId);
            }
          } else {
            // Zero-total bookings must not create Stripe payment intents.
            setClientSecret(null);
            setPaymentIntent(null);
          }

          let totalItems = [
            {
              title: t('trip:tripTotal'),
              priceperperson: totalWithFees,
              occupancy: 1,
              depositamount: totalDeposit,
              hideTotalBreakdown: true,
              hideDeposit: true,
              selectedQuantity: 1,
              key: 'trip-total',
              totalPrice: totalWithFees,
              order: 3,
              visible: true
            },
            {
              title: t('trip:tripSubTotal'),
              priceperperson: totalDeposit > 0 ? totalDeposit : totalPrice,
              occupancy: 1,
              depositamount: 0,
              hideTotalBreakdown: true,
              hideDeposit: true,
              selectedQuantity: 1,
              key: 'trip-subtotal',
              totalPrice: totalDeposit > 0 ? totalDeposit : totalPrice,
              order: 1,
              visible: totalFees > 0
            },
            {
              title: t('trip:processingFee'),
              priceperperson: totalFees,
              occupancy: 1,
              depositamount: 0,
              hideTotalBreakdown: true,
              hideDeposit: true,
              selectedQuantity: 1,
              key: 'trip-totalfees',
              totalPrice: totalFees,
              order: 2,
              visible: totalFees > 0
            }
          ];

          dispatch(addTotalItems({ totalItems }));
          dispatch(updateActiveStep({ key: 'payment' }));
          scrollToStepper(); // Scroll to stepper after navigating to payment
        } else {
          ErrorToast(t('errors:bookingCanNotContinue'));
        }
      } else {
        ErrorToast(t('errors:bookingNeedsAtLeastOnePlan'));
      }
    } catch (err) {
      ErrorToast(t('errors:bookingUnexpectedError'));
    } finally {
      dispatch(setIsSubmitting(false));
    }
  };

  const onNavigateToPassengers = async () => {
    try {
      dispatch(setIsSubmitting(true));

      if (selectedPlans.length) {
        toggleFeeTotalsVisibility();

        const { data = [] } = await getTripPassengerFields(trip?.tripid);

        if (data && data?.length > 0) {
          generatePassengerForms(data);
        } else {
          ErrorToast(t('errors:bookingUnexpectedError'));
        }

        dispatch(updateActiveStep({ key: 'passenger' }));
      } else {
        ErrorToast(t('errors:bookingNeedsAtLeastOnePlan'));
      }
    } catch (error) {
      generatePassengerForms([]);
    } finally {
      dispatch(setIsSubmitting(false));
      scrollToStepper();
    }
  };

  //  Remove accents and special characters and generate passenger form key
  const generateKey = (text = '') => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, '');
  };

  const generateFormUuid = () => {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const generatePassengerForms = (fieldsList = []) => {
    const passengerForms = [];

    const filteredFieldSettings = fieldsList.filter(
      (field) =>
        field?.isenabled &&
        !field?.ishidden &&
        !field?.isinternal &&
        isPassengerFieldVisibleForBooking(field, {
          enablePassengerFieldUploads,
          enablePaxWaivers
        }) &&
        (field?.isoptional || field?.isrequired)
    );

    const mappedFields = filteredFieldSettings?.map((field, index) => {
      return {
        fieldId: index + 1,
        key: generateKey(field?.title ?? `passengerfield${index + 1}`),
        ...field,
        error: null,
        value: ''
      };
    });

    selectedPlans?.forEach((plan) => {
      const indexes = Array(plan?.selectedQuantity).fill(0);

      indexes.forEach((_, index) => {
        const filteredForms = booking?.passengerForms
          ?.filter((form) => form?.tripplanid === plan?.tripplanid)
          ?.sort((a, b) => a.order - b.order);

        if (index < filteredForms?.length) {
          const existingForm = filteredForms[index];

          // Filter existing form fields to only include those that match current filteredFieldSettings
          const filteredExistingFields =
            existingForm?.fields?.filter((field) => {
              return filteredFieldSettings?.some(
                (setting) =>
                  setting.trippassengerfieldid === field.trippassengerfieldid
              );
            }) || [];

          // Update field properties from current settings while preserving values
          const updatedFields = filteredExistingFields.map((existingField) => {
            const currentSetting = filteredFieldSettings.find(
              (setting) =>
                setting.trippassengerfieldid ===
                existingField.trippassengerfieldid
            );

            if (currentSetting) {
              return {
                ...existingField,
                // Update field properties from current settings
                title: currentSetting.title,
                description: currentSetting.description,
                isrequired: currentSetting.isrequired,
                isoptional: currentSetting.isoptional,
                fieldtype: currentSetting.fieldtype,
                sequence: currentSetting.sequence,
                trippassengerfieldconfig:
                  currentSetting.trippassengerfieldconfig,
                trippassengerfieldoption:
                  currentSetting.trippassengerfieldoption,
                // Preserve existing values and errors
                value: existingField.value || '',
                error: existingField.error || null
              };
            }

            return existingField;
          });

          // Add any new fields that weren't in the existing form
          const newFields = filteredFieldSettings
            .filter(
              (setting) =>
                !filteredExistingFields.some(
                  (field) =>
                    field.trippassengerfieldid === setting.trippassengerfieldid
                )
            )
            .map((field, idx) => ({
              fieldId: filteredExistingFields.length + idx + 1,
              key: generateKey(
                field?.title ??
                  `passengerfield${filteredExistingFields.length + idx + 1}`
              ),
              ...field,
              error: null,
              value: ''
            }));

          // Combine updated existing fields with new fields and sort by sequence
          const allFields = [...updatedFields, ...newFields].sort(
            (a, b) => a.seq - b.seq
          );

          passengerForms.push({
            ...existingForm,
            uuid: existingForm?.uuid || generateFormUuid(),
            fields: allFields
          });
        } else {
          // Create new form with all current fields
          passengerForms.push({
            uuid: generateFormUuid(),
            order: index + 1,
            formId: `${plan.tripplanid}-${index + 1}`,
            tripplanid: plan.tripplanid,
            title: plan.title,
            fields: mappedFields.sort((a, b) => a.seq - b.seq)
          });
        }
      });
    });

    const nextUploadKeys = new Set();
    passengerForms.forEach((form) => {
      form?.fields?.forEach((field) => {
        if (isFileUploadPassengerFieldType(field?.fieldtype)) {
          nextUploadKeys.add(
            getPassengerFieldUploadKey(
              form?.formId,
              field?.trippassengerfieldid
            )
          );
        }
      });
    });

    setPassengerFieldFiles((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([uploadKey]) =>
          nextUploadKeys.has(uploadKey)
        )
      )
    );

    dispatch(updatePassengerForms({ passengerForms }));
  };

  const onNavigateToAddOnsOrPayment = async (event) => {
    // Check if add-ons step should be shown
    const addonsStep = steps.find((s) => s.key === 'addons');
    if (addonsStep?.shouldRender) {
      // Navigate to add-ons step
      dispatch(updateActiveStep({ key: 'addons' }));
      scrollToStepper();
    } else {
      // Skip add-ons and go directly to payment
      await onNavigateToPayment(event);
    }
  };

  const onNavigateToPlans = () => {
    toggleFeeTotalsVisibility();
    dispatch(updateActiveStep({ key: 'plan' }));
    scrollToStepper(); // Scroll to stepper after navigating to plans
  };

  const onCancelBooking = async () => {
    if (
      isSubmitting ||
      isProcessing ||
      isRestoringIntent ||
      currencyState.loading
    )
      return;
    if (activeStep?.key === 'payment' && !(await currencyState.reset())) return;
    dispatch(resetTotals());
    dispatch(resetCheckout());
    setPassengerFieldFiles({});
    updateIsOpen(false);
  };

  const isCustomFormValid = () => {
    return (
      customForm.email.trim() !== '' &&
      customForm.firstName.trim() !== '' &&
      customForm.lastName.trim() !== ''
    );
  };

  const isCTADisabled = () => {
    switch (activeStep?.key) {
      case 'success':
        return true;
      case 'passenger':
        return getFeedbackFromForms()?.length > 0;
      case 'plan':
        return (
          selectedPlans.length === 0 ||
          isTripDeparted ||
          (enableTripCapacity && capacityExceeded)
        );
      case 'payment': {
        return (
          isProcessing ||
          isSubmitting ||
          isRestoringIntent ||
          (isStripePaymentRequired &&
            !paymentRecoveryStatus &&
            (currencyState.loading || currencyState.paymentBlocked)) ||
          (isStripePaymentRequired &&
            (!isPaymentMethodComplete ||
              Number(booking?.amountToCharge || 0) <= 0)) ||
          (requiresSignedDocument && documentStatus != 'COMPLETED') ||
          (shouldRequireTerms && !isTermsChecked) ||
          !isCustomFormValid()
        );
      }

      default:
        return false;
    }
  };

  const validateCustomForm = () => {
    dispatch(
      setCustomFormError({
        email: '',
        firstName: '',
        lastName: ''
      })
    );

    let errors = { ...customFormError };

    if (customForm.email.trim() === '') {
      errors = { ...errors, email: t('trip:yourEmailIsRequired') };
    } else {
      if (!isEmailValid(customForm.email)) {
        errors = { ...errors, email: t('trip:pleaseEnterValidEmail') };
      }
    }

    if (customForm.firstName.trim() === '') {
      errors = { ...errors, firstName: t('trip:yourFirstNameIsRequired') };
    }

    if (customForm.lastName.trim() === '') {
      errors = { ...errors, lastName: t('trip:yourLastNameIsRequired') };
    }

    const isValidForm =
      errors.email === '' && errors.firstName === '' && errors.lastName === '';
    if (!isValidForm) {
      dispatch(setCustomFormError(errors));
    }

    return isValidForm;
  };

  const onCompletePayment = (evt) => {
    preventBubbling(evt);
    if (isCTADisabled()) return;
    const isValid = validateCustomForm();
    dispatch(setTermsError(''));
    dispatch(setSignedDocumentError(''));

    if (isValid) {
      if (requiresSignedDocument) {
        if (signedDocumentId) {
          if (documentStatus === 'COMPLETED') {
            if (submitRef.current) {
              submitRef.current.click();
            }
          }
        } else {
          dispatch(setSignedDocumentError(t('trip:mustSignDocument')));
        }
      } else {
        if (!shouldRequireTerms || isTermsChecked) {
          if (submitRef.current) {
            submitRef.current.click();
          }
        } else {
          dispatch(setTermsError(t('trip:mustAcceptTermsAndConditions')));
        }
      }
    }
  };

  const svgString = encodeURIComponent(
    renderToStaticMarkup(<PlaceholderLogo />)
  );

  const svgLogoUrl = `data:image/svg+xml,${svgString}`;

  return (
    <Box id={id} data-testid='book-now-modal-component-container' {...rest}>
      <XGModal
        allowClosing={true}
        data-testid='book-now-modal-component'
        dismissable={false}
        id='book-now-modal'
        open={isOpenModal}
        className={`${activeStep?.key == 'success' ? 'book-now-modal--success' : ''}`}
        title={
          activeStep?.key != 'success' && (
            <Box style={{ borderBottom: '1px solid #e5e7eb' }}>
              <Box className='book-now-modal__header'>
                <Box>
                  <img
                    src={tenantLogo ? tenantLogo : svgLogoUrl}
                    alt={tenantName}
                    style={{ height: '40px' }}
                    draggable='false'
                  />
                </Box>
                <Box className='book-now-modal__header-title'>
                  <h1 className='text-xl-semibold'>
                    {activeStep?.key !== 'success' ? tripName : ''}{' '}
                  </h1>
                  {startDate && (
                    <Box
                      className='booking-modal-output-header__label'
                      style={{ display: 'flex' }}
                    >
                      <p className='text-sm-regular booking-modal-output-header__label-text'>
                        {`${format(new Date(startDate), dateFormat)} - ${
                          endDate && format(new Date(endDate), dateFormat)
                        }`}
                      </p>
                    </Box>
                  )}
                </Box>
              </Box>
              {activeStep?.key !== 'success' && (
                <BookingStepper
                  id='book-now-modal-stepper'
                  steps={filteredSteps}
                  onStepClick={handleStepClick}
                />
              )}
            </Box>
          )
        }
        labelledby={tripName}
        maxWidth='xl'
        scroll='paper'
        onClose={() => {
          onCancelBooking();
        }}
      >
        <Box className='book-now-modal__container'>
          {activeStep?.key === 'success' ? (
            <BookingSuccessful id='payment-successfull-message' />
          ) : (
            <Box className='book-now-modal__booking'>
              {activeStep?.key === 'plan' ? (
                <BookingPlan
                  id='booking-plan-step'
                  bookingPlans={bookingplans}
                  tripCapacity={enableTripCapacity ? tripCapacity : null}
                  isFullCapacity={isFullCapacity}
                  isTripDeparted={isTripDeparted}
                  usedCapacity={enableTripCapacity ? usedCapacity : 0}
                  showCapacityBanner={enableTripCapacity}
                  capacityBannerProps={{
                    BannerComponent: BookingCapacityBanner,
                    remainingCapacity: originalRemainingCapacity,
                    isExceeded: capacityExceeded,
                    totalSelected: 0 // Don't show user selections in banner
                  }}
                />
              ) : activeStep?.key === 'passenger' ? (
                <>
                  <BookingPassengerPanel
                    id='booking-passenger-panel'
                    passengerFieldFiles={passengerFieldFiles}
                    onPassengerFieldFilesChange={
                      handlePassengerFieldFilesChange
                    }
                  />
                </>
              ) : activeStep?.key === 'addons' ? (
                <>
                  <BookingAddOnsPanel id='booking-addons-panel' />
                </>
              ) : (
                <>
                  <BookingPayment
                    id='booking-payment-step'
                    stripePromise={stripePromise}
                    clientSecret={clientSecret}
                    paymentIntentId={paymentIntent}
                    paymentMethodOrder={effectivePaymentMethods}
                    selectedCurrency={selectedCurrency}
                    currencyState={currencyState}
                    onPaymentMethodChange={setPaymentMethod}
                    onPaymentRecoveryChange={setPaymentRecoveryStatus}
                    submitRef={submitRef}
                    passengerFieldFiles={passengerFieldFiles}
                    isZeroTotalBooking={isZeroTotalBooking}
                  />
                </>
              )}
              <Box className='book-now-modal___right-panel'>
                <BookingTotals
                  id='booking-totals-panel'
                  currencyState={isStripePaymentRequired ? currencyState : null}
                  operatorCurrency={operatorCurrency}
                  paymentMethod={paymentMethod}
                  paymentMethodOrder={effectivePaymentMethods}
                />
                <BookingPlanActions
                  onCancel={(evt) => {
                    activeStep?.key === 'plan'
                      ? () => {}
                      : activeStep?.key === 'passenger'
                        ? onNavigateToPlans()
                        : activeStep?.key === 'addons'
                          ? handleStepClick(evt, 1)
                          : handleStepClick(evt, 1);
                  }}
                  onHandleNext={
                    activeStep?.key === 'plan'
                      ? onNavigateToPassengers
                      : activeStep?.key === 'passenger'
                        ? onNavigateToAddOnsOrPayment
                        : activeStep?.key === 'addons'
                          ? onNavigateToPayment
                          : onCompletePayment
                  }
                  selectedCurrency={selectedCurrency}
                  paymentRecoveryStatus={paymentRecoveryStatus}
                  isSubmitting={isSubmitting || isRestoringIntent}
                  disabledBack={currencyState.loading}
                  disabledConfirm={isCTADisabled()}
                  isFullCapacity={isFullCapacity}
                  operatorEmail={trip?.operatorEmail}
                  operatorContact={operatorContact}
                />
                {hasUnavailablePlans &&
                  !isFullCapacity &&
                  operatorContactLink && (
                    <div className='booking-totals__unavailableplans'>
                      <WarningCircle size={20} fill='#92400E' />
                      <div className=''>
                        <div className='booking-totals__unavailableplans-title'>
                          {t('trip:unavailableWarningTitle')}
                        </div>
                        <div className='booking-totals__unavailableplans-text'>
                          {t('trip:unavailableWarningText')}
                          <br />
                          <a href={operatorContactLink.href} className=''>
                            {operatorContactLink.label}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
              </Box>
            </Box>
          )}
        </Box>
      </XGModal>
    </Box>
  );
};

BookingModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpenModal: PropTypes.bool,
  updateIsOpen: PropTypes.func,
  bookingPlans: PropTypes.array
};

export default BookingModal;
